import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/visits';
const PAGE_SIZE_DEFAULT = 20;

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'InProgress', label: 'Devam Eden' },
  { key: 'Completed', label: 'Tamamlanan' },
  { key: 'Cancelled', label: 'İptal' },
];

const ROUTE_TYPE_TABS = [
  { key: 'all', label: 'Tüm Ziyaretler' },
  { key: 'route', label: 'Rota İçi' },
  { key: 'nonRoute', label: 'Rota Dışı' },
];

const STATUS_META = {
  InProgress: { label: 'Devam Ediyor', tone: 'wait' },
  Completed: { label: 'Tamamlandı', tone: 'ok' },
  Cancelled: { label: 'İptal', tone: 'bad' },
};

const STATUS_OPTIONS = [
  { value: 'InProgress', label: 'Devam Ediyor' },
  { value: 'Completed', label: 'Tamamlandı' },
  { value: 'Cancelled', label: 'İptal' },
];

const ROUTE_TYPE_OPTIONS = [
  { value: 'route', label: 'Rota İçi' },
  { value: 'nonRoute', label: 'Rota Dışı' },
];

// REST endpoint (OData değil) → kolon filtreleri istemci tarafında uygulanır.
// odataField alanları client-side eşleştirme için kullanılır (camelCase satır anahtarları).
const COLUMN_FILTER_META = {
  customerTitle: { type: 'text', odataField: 'customerTitle' },
  representativeName: { type: 'text', odataField: 'representativeName' },
  routeType: { type: 'select', odataField: 'routeType', valueFormat: 'string', options: ROUTE_TYPE_OPTIONS },
  status: { type: 'select', odataField: 'status', valueFormat: 'string', options: STATUS_OPTIONS },
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    todayCount: pick(data, 'todayCount', 'TodayCount') ?? 0,
    completedCount: pick(data, 'completedCount', 'CompletedCount') ?? 0,
    inProgressCount: pick(data, 'inProgressCount', 'InProgressCount') ?? 0,
    cancelledCount: pick(data, 'cancelledCount', 'CancelledCount') ?? 0,
    nonRouteCount: pick(data, 'nonRouteCount', 'NonRouteCount') ?? 0,
  };
}

function statusBadgeFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function routeTypeBadge(routeId) {
  const nonRoute = routeId == null;
  return <StatusBadge tone={nonRoute ? 'wait' : 'neutral'}>{nonRoute ? 'Rota Dışı' : 'Rota İçi'}</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

function formatDuration(startedAt, endedAt) {
  if (!startedAt) return '-';
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '-';
  const totalMin = Math.round((end - start) / 60000);
  if (totalMin < 60) return `${totalMin} dk`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h} sa` : `${h} sa ${m} dk`;
}

function toIsoStart(dateStr) {
  if (!dateStr) return '';
  try { return new Date(`${dateStr}T00:00:00`).toISOString(); } catch { return ''; }
}

function toIsoEnd(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  } catch { return ''; }
}

// İstemci tarafı kolon filtresi: ColumnFilterPopover { op, value, value2 } sözleşmesi.
function matchColumnFilter(meta, filter, raw) {
  if (!filter) return true;
  const op = filter.op;
  const v = filter.value;
  const v2 = filter.value2;
  if (op === 'blank') return raw === null || raw === undefined || raw === '';
  if (op === 'notBlank') return !(raw === null || raw === undefined || raw === '');

  if (meta.type === 'text') {
    const cell = String(raw ?? '').toLocaleLowerCase('tr-TR');
    const needle = String(v ?? '').toLocaleLowerCase('tr-TR');
    if (op === 'contains') return cell.includes(needle);
    if (op === 'notContains') return !cell.includes(needle);
    if (op === 'equals') return cell === needle;
    if (op === 'notEquals') return cell !== needle;
    if (op === 'startsWith') return cell.startsWith(needle);
    if (op === 'endsWith') return cell.endsWith(needle);
    return true;
  }
  if (meta.type === 'number') {
    const cell = Number(raw);
    const n = Number(v);
    if (op === 'equals') return cell === n;
    if (op === 'notEquals') return cell !== n;
    if (op === 'gt') return cell > n;
    if (op === 'gte') return cell >= n;
    if (op === 'lt') return cell < n;
    if (op === 'lte') return cell <= n;
    if (op === 'between') return cell >= n && cell <= Number(v2);
    return true;
  }
  if (meta.type === 'select') {
    return String(raw ?? '') === String(v ?? '');
  }
  return true;
}

export function VisitsPage() {
  usePageMeta('Ziyaretler', 'Saha temsilcilerinin müşteri ziyaretleri (rota içi / rota dışı)');

  const [statusFilter, setStatusFilter] = useState('');
  const [routeType, setRouteType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ todayCount: 0, completedCount: 0, inProgressCount: 0, cancelledCount: 0, nonRouteCount: 0 });
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [toast, setToast] = useState(null);

  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [sorting, setSorting] = useState([{ id: 'startedAt', desc: true }]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState({});

  const [selected, setSelected] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (routeType === 'nonRoute') params.set('nonRoute', 'true');
      if (routeType === 'route') params.set('nonRoute', 'false');
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        todayCount: data.todayCount,
        completedCount: data.completedCount,
        inProgressCount: data.inProgressCount,
        cancelledCount: data.cancelledCount,
        nonRouteCount: data.nonRouteCount,
      });
      setCurrentPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, routeType, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback((row) => {
    setSelected(row);
    setDetailRow(row);
  }, []);

  const closeDetail = () => setDetailRow(null);

  // === İstemci tarafı serbest metin + kolon filtreleri ===
  const filteredRows = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    return rows.filter((row) => {
      if (needle) {
        const fields = [
          pick(row, 'customerTitle', 'CustomerTitle'),
          pick(row, 'representativeName', 'RepresentativeName'),
          pick(row, 'notes', 'Notes'),
          pick(row, 'reasonCode', 'ReasonCode'),
        ];
        if (!fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle))) return false;
      }
      for (const [field, filter] of Object.entries(columnFilters)) {
        const meta = COLUMN_FILTER_META[field];
        if (!meta) continue;
        if (field === 'routeType') {
          const rt = pick(row, 'routeId', 'RouteId') == null ? 'nonRoute' : 'route';
          if (!matchColumnFilter(meta, filter, rt)) return false;
        } else if (!matchColumnFilter(meta, filter, row[meta.odataField])) {
          return false;
        }
      }
      return true;
    });
  }, [rows, filterText, columnFilters]);

  const applyFilterText = (text) => { setFilterText(text); setCurrentPage(1); };
  const setColumnFilter = (columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('startedAt', {
      header: 'Başlangıç', enableColumnFilter: false,
      sortingFn: (a, b) => {
        const av = a.original.startedAt ? new Date(a.original.startedAt).getTime() : 0;
        const bv = b.original.startedAt ? new Date(b.original.startedAt).getTime() : 0;
        return av - bv;
      },
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('customerTitle', {
      header: 'Müşteri', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '(Bilinmiyor)',
    }),
    columnHelper.accessor('representativeName', {
      header: 'Temsilci', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('routeId', {
      id: 'routeType', header: 'Tip', meta: { filterType: 'select' },
      enableSorting: false,
      cell: (info) => routeTypeBadge(info.getValue()),
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => statusBadgeFor(info.getValue()),
    }),
    columnHelper.display({
      id: 'duration', header: 'Süre',
      cell: ({ row }) => formatDuration(pick(row.original, 'startedAt', 'StartedAt'), pick(row.original, 'endedAt', 'EndedAt')),
    }),
    columnHelper.display({
      id: 'gps', header: 'GPS',
      cell: ({ row }) => {
        const lat = pick(row.original, 'startLatitude', 'StartLatitude');
        const lng = pick(row.original, 'startLongitude', 'StartLongitude');
        if (lat == null || lng == null) return <span style={{ color: '#94a3b8' }}>-</span>;
        return (
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            Haritada Aç
          </a>
        );
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [openDetail]);

  const totalCount = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex: currentPage - 1, pageSize }) : updater;
      setCurrentPage(next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const pageRowCount = table.getRowModel().rows.length;

  const handleExport = () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      if (filteredRows.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const cols = [
        { headerName: 'Başlangıç', valueGetter: ({ row }) => formatDate(pick(row, 'startedAt', 'StartedAt')) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '' },
        { headerName: 'Temsilci', valueGetter: ({ row }) => pick(row, 'representativeName', 'RepresentativeName') || '' },
        { headerName: 'Tip', valueGetter: ({ row }) => (pick(row, 'routeId', 'RouteId') == null ? 'Rota Dışı' : 'Rota İçi') },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[pick(row, 'status', 'Status')]?.label || pick(row, 'status', 'Status') || '') },
        { headerName: 'Süre', valueGetter: ({ row }) => formatDuration(pick(row, 'startedAt', 'StartedAt'), pick(row, 'endedAt', 'EndedAt')) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredRows, cols, `ziyaretler_${dateStr}`);
      showToast('success', `${filteredRows.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      {STATUS_TABS.map((tab) => (
        <button
          key={tab.key || 'all'}
          type="button"
          onClick={() => setStatusFilter(tab.key)}
          style={{
            ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary),
            padding: '6px 14px', fontSize: 13,
          }}
        >
          {tab.label}
        </button>
      ))}
      <span style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
      {ROUTE_TYPE_TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => setRouteType(tab.key)}
          style={{
            ...(routeType === tab.key ? s.btnPrimary : s.btnSecondary),
            padding: '6px 14px', fontSize: 13,
          }}
        >
          {tab.label}
        </button>
      ))}
      <input
        type="date"
        value={dateFrom}
        onChange={(e) => setDateFrom(e.target.value)}
        style={{ ...s.filterInput, width: 140 }}
        title="Başlangıç tarihi"
      />
      <input
        type="date"
        value={dateTo}
        onChange={(e) => setDateTo(e.target.value)}
        style={{ ...s.filterInput, width: 140 }}
        title="Bitiş tarihi"
      />
      <input
        style={s.filterInput}
        type="text"
        placeholder="Müşteri / temsilci / not..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load} disabled={loading}>{loading ? 'Yükleniyor...' : 'Yenile'}</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pageRowCount}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  const metaNode = (
    <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <StatusBadge tone="neutral">Bugün: {counts.todayCount}</StatusBadge>
      <StatusBadge tone="wait">Devam Eden: {counts.inProgressCount}</StatusBadge>
      <StatusBadge tone="ok">Tamamlanan: {counts.completedCount}</StatusBadge>
      <StatusBadge tone="bad">İptal: {counts.cancelledCount}</StatusBadge>
      <StatusBadge tone="neutral">Rota Dışı: {counts.nonRouteCount}</StatusBadge>
    </span>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Ziyaretler"
        subtitle="Saha temsilcilerinin müşteri ziyaretleri. Rota dışı ziyaretler şef onayı ile başlatılır (Onay Talepleri sayfası); rota içi ziyaretler günlük rotadan gelir. Mobile'dan push ile gelir; admin görüntüleme."
        meta={metaNode}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={closeDetail}>
          <div style={{ ...s.modal, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {pick(detailRow, 'customerTitle', 'CustomerTitle') || 'Ziyaret Detayı'}
              </h3>
              <button type="button" style={s.closeBtn} onClick={closeDetail}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Temsilci</label>
                  <div style={s.inputView}>
                    {pick(detailRow, 'representativeName', 'RepresentativeName') || '-'}
                  </div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {statusBadgeFor(pick(detailRow, 'status', 'Status'))}
                    {routeTypeBadge(pick(detailRow, 'routeId', 'RouteId'))}
                  </div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Başlangıç</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'startedAt', 'StartedAt'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Bitiş</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'endedAt', 'EndedAt'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Süre</label>
                  <div style={s.inputView}>
                    {formatDuration(pick(detailRow, 'startedAt', 'StartedAt'), pick(detailRow, 'endedAt', 'EndedAt'))}
                  </div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>GPS Doğruluğu</label>
                  <div style={s.inputView}>
                    {pick(detailRow, 'gpsAccuracyMeters', 'GpsAccuracyMeters') != null
                      ? `${pick(detailRow, 'gpsAccuracyMeters', 'GpsAccuracyMeters')} m`
                      : '-'}
                  </div>
                </div>
              </div>
              {(pick(detailRow, 'startLatitude', 'StartLatitude') != null ||
                pick(detailRow, 'endLatitude', 'EndLatitude') != null) && (
                <div style={s.formGroup}>
                  <label style={s.label}>Konum</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {pick(detailRow, 'startLatitude', 'StartLatitude') != null && (
                      <a
                        href={`https://www.google.com/maps?q=${pick(detailRow, 'startLatitude', 'StartLatitude')},${pick(detailRow, 'startLongitude', 'StartLongitude')}`}
                        target="_blank" rel="noreferrer"
                        style={{ ...s.btnSecondary, textDecoration: 'none', display: 'inline-block' }}
                      >
                        Başlangıç GPS
                      </a>
                    )}
                    {pick(detailRow, 'endLatitude', 'EndLatitude') != null && (
                      <a
                        href={`https://www.google.com/maps?q=${pick(detailRow, 'endLatitude', 'EndLatitude')},${pick(detailRow, 'endLongitude', 'EndLongitude')}`}
                        target="_blank" rel="noreferrer"
                        style={{ ...s.btnSecondary, textDecoration: 'none', display: 'inline-block' }}
                      >
                        Bitiş GPS
                      </a>
                    )}
                  </div>
                </div>
              )}
              {pick(detailRow, 'reasonCode', 'ReasonCode') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Sebep Kodu</label>
                  <div style={s.inputView}>{pick(detailRow, 'reasonCode', 'ReasonCode')}</div>
                </div>
              )}
              {pick(detailRow, 'notes', 'Notes') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Notlar</label>
                  <div style={s.textareaView}>{pick(detailRow, 'notes', 'Notes')}</div>
                </div>
              )}
              {pick(detailRow, 'distanceMeters', 'DistanceMeters') != null && (
                <div style={s.formGroup}>
                  <label style={s.label}>Mesafe</label>
                  <div style={s.inputView}>{pick(detailRow, 'distanceMeters', 'DistanceMeters')} m</div>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeDetail}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default VisitsPage;
